import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import { Store } from '@ngxs/store';
import {
  catchError,
  delay,
  filter,
  map,
  of,
  shareReplay,
  Subject,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { AsyncPipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  TuiButtonModule,
  TuiDialogService,
  TuiSvgModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { AlertService } from '@shared/services/alert.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import _ from 'lodash';
import { TuiActiveZoneModule, TuiLetModule } from '@taiga-ui/cdk';
import { SpaceNumberPipe } from '@shared/pipes/space-number.pipe';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import {
  AttachmentsManagerComponent,
  AttachmentsManagerInputData,
} from '@shared/components/attachments-manager/attachments-manager.component';
import {
  TuiBadgedContentModule,
  TuiBadgeNotificationModule,
} from '@taiga-ui/experimental';
import { getLetterByIndex } from '@shared/utils/common';
import { TuiInputModule } from '@taiga-ui/kit';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  Cargo,
  CargoLoadingAddress,
  CargoLoadingAddressForm,
  CargoUnloadingAddress,
} from '../../../../types/cargo';
import { CargoLoadingService } from '../../services/cargo-loading.service';
import { CargoLoadingState } from '../../store/cargo-loading.state';
import { CargoLoadingReplaceLoadingAddressesJson } from '../../store/cargo-loading.actions';
import { getBoxAndPlaceQuantityInGroup } from '../../utils/box-place-quantity';
import { DriversReportService } from '../../services/drivers-report.service';

@Component({
  selector: 'app-loading-addresses',
  standalone: true,
  imports: [
    AsyncPipe,
    CdkDrag,
    CdkDropList,
    TuiButtonModule,
    TuiLetModule,
    DecimalPipe,
    SpaceNumberPipe,
    TuiBadgeNotificationModule,
    TuiBadgedContentModule,
    TuiBadgedContentModule,
    TuiSvgModule,
    UpperCasePipe,
    TuiInputModule,
    ReactiveFormsModule,
    TuiTextfieldControllerModule,
    TuiActiveZoneModule,
  ],
  templateUrl: './loading-addresses.component.html',
  styleUrl: './loading-addresses.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadingAddressesComponent implements OnInit {
  cargo$ = this.store.select(CargoLoadingState.getSelectedCargo);
  generateDriversReport$ = new Subject<number>();
  update$ = new Subject<void>();
  loadingAddressesForm = this.fb.array<FormGroup<CargoLoadingAddressForm>>([]);

  boxAndPlaceQuantityPerFarm$ = this.store
    .select(CargoLoadingState.getOrders)
    .pipe(
      map(preOrders => {
        const groupByFarmName = _.groupBy(preOrders, preOrder => {
          return preOrder.farmJson.name;
        });
        return getBoxAndPlaceQuantityInGroup(groupByFarmName);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  updateSort$ = new Subject<
    Pick<CdkDragDrop<CargoLoadingAddress>, 'currentIndex' | 'previousIndex'>
  >();

  openAttachmentsDialog$ = new Subject<{
    attachmentId: Nullable<number>;
    index: number;
  }>();

  protected readonly getLetterByIndex = getLetterByIndex;
  orders$ = this.store.select(CargoLoadingState.getOrders);
  constructor(
    private readonly store: Store,
    protected readonly cargoLoadingService: CargoLoadingService,
    private readonly alert: AlertService,
    private readonly destroyRef: DestroyRef,
    private readonly dialogs: TuiDialogService,
    private readonly driversReport: DriversReportService,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  onUpdateSort() {
    this.updateSort$
      .pipe(
        filter(params => params.currentIndex !== params.previousIndex),
        withLatestFrom(this.cargo$),
        switchMap(([{ previousIndex, currentIndex }, initialCargo]) => {
          if (!initialCargo) {
            return of(null);
          }
          const loadingAddressesJson = structuredClone(
            this.loadingAddressesForm.getRawValue(),
          );

          moveItemInArray(loadingAddressesJson, previousIndex, currentIndex);
          this.replaceLoadingAddresses(loadingAddressesJson);
          const cargo: Cargo = {
            ...initialCargo,
            loadingAddressesJson,
          };
          return this.cargoLoadingService.updateCargo(cargo).pipe(
            tap(() => {
              this.alert.showSuccess('Сортировка сохранена');
            }),
            catchError(() => {
              this.replaceLoadingAddresses(
                initialCargo.loadingAddressesJson ?? [],
              );
              this.alert.showError();
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onAttachments() {
    this.openAttachmentsDialog$
      .pipe(
        withLatestFrom(this.cargo$, this.cargoLoadingService.readonly$),
        switchMap(([{ attachmentId, index }, cargo, readonly]) => {
          const data: AttachmentsManagerInputData = {
            cargoId: cargo?.id ?? 0,
            attachmentId,
            canDelete: !readonly,
            canUpload: !readonly,
          };
          return this.dialogs
            .open<Nullable<number>>(
              new PolymorpheusComponent(AttachmentsManagerComponent),
              {
                size: 'auto',
                data,
              },
            )
            .pipe(
              map(updatedAttachmentId => {
                return {
                  updatedAttachmentId,
                  attachmentId,
                  index,
                };
              }),
            );
        }),
        withLatestFrom(this.cargo$),
        switchMap(([{ updatedAttachmentId, index }, cargo]) => {
          const loadingAddressesJson =
            cargo?.loadingAddressesJson.map((address, addressIndex) => {
              return addressIndex === index
                ? { ...address, attachmentId: updatedAttachmentId }
                : address;
            }) ?? [];
          const payload: Cargo = {
            ...cargo,
            loadingAddressesJson,
          } as Cargo;
          return this.cargoLoadingService.updateCargo(payload).pipe(
            tap(() => {
              this.alert.showSuccess();
            }),
          );
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onGenerateDriversReport() {
    this.generateDriversReport$
      .pipe(
        withLatestFrom(this.cargo$, this.orders$),
        map(([selectedAddressIndex, cargo, orders]) => ({
          cargo,
          orders,
          selectedAddressIndex,
        })),
        filter(data => !!data.cargo),
        switchMap(({ cargo, orders, selectedAddressIndex }) =>
          this.driversReport.generate(
            cargo as Cargo,
            orders,
            selectedAddressIndex,
          ),
        ),
        catchError(e => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onZoneChange(focused: boolean) {
    if (!focused) {
      this.update$.next();
    }
  }

  fillFormOnStateChange() {
    this.cargo$
      .pipe(
        withLatestFrom(this.cargoLoadingService.readonly$),
        tap(([cargo, readonly]) => {
          const addresses = cargo?.loadingAddressesJson ?? [];
          this.loadingAddressesForm.clear({ emitEvent: false });
          addresses.forEach((address, index, items) => {
            const group = this.fb.group<CargoLoadingAddressForm>({
              address: this.fb.control(address.address),
              attachmentId: this.fb.control(address.attachmentId),
              comment: this.fb.control(address.comment),
              farmName: this.fb.control(address.farmName),
              latitude: this.fb.control(address.latitude),
              longitude: this.fb.control(address.longitude),
            });

            if (readonly) {
              group.disable();
            }
            this.loadingAddressesForm.push(group, {
              emitEvent: index === items.length - 1,
            });
          });
          this.cdr.markForCheck();
        }),
      )
      .subscribe();
  }

  replaceLoadingAddresses(addresses: CargoLoadingAddress[]) {
    this.store.dispatch(new CargoLoadingReplaceLoadingAddressesJson(addresses));
  }

  drop({ previousIndex, currentIndex }: CdkDragDrop<CargoUnloadingAddress[]>) {
    this.updateSort$.next({ previousIndex, currentIndex });
  }

  updateOnChanges() {
    this.update$
      .pipe(
        delay(0),
        filter(() => !this.loadingAddressesForm.pristine),
        withLatestFrom(this.cargo$),
        switchMap(([, cargo]) => {
          const loadingAddressesJson = this.loadingAddressesForm.getRawValue();

          return this.cargoLoadingService.updateCargo({
            ...(cargo as Cargo),
            loadingAddressesJson,
          });
        }),
        tap(() => {
          this.alert.showSuccess();
          this.loadingAddressesForm.markAsUntouched();
          this.loadingAddressesForm.markAsPristine();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
      )
      .subscribe();
  }

  disableWhenReadonly() {
    this.cargoLoadingService.readonly$
      .pipe(
        tap(readonly => {
          if (readonly) {
            this.loadingAddressesForm.disable();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.fillFormOnStateChange();
    this.updateOnChanges();
    this.onUpdateSort();
    this.onAttachments();
    this.onGenerateDriversReport();
    this.disableWhenReadonly();
  }
}
